/**
 * MarketingLayout — shared header + footer for the public marketing pages
 * (Home, Features, Pricing, FAQ).
 *
 * @module components/MarketingLayout
 */

import type { ReactElement, ReactNode } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Logo } from './Logo';
import { SiteFooter } from './SiteFooter';

const navLinkClass = ({ isActive }: { isActive: boolean }): string =>
  isActive ? 'text-brand-700 font-semibold' : 'hover:text-gray-900';

export function MarketingLayout({ children }: { children: ReactNode }): ReactElement {
  return (
    <div className="flex min-h-screen flex-col bg-white text-gray-900">
      {/* Nav */}
      <header className="sticky top-0 z-20 border-b border-gray-100 bg-white/90 backdrop-blur">
        <nav className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3.5">
          <Link to="/" aria-label="Ballast Payroll home">
            <Logo size="xs" />
          </Link>
          <div className="hidden items-center gap-7 text-sm font-medium text-gray-700 md:flex">
            <NavLink to="/features" className={navLinkClass}>Features</NavLink>
            <NavLink to="/pricing" className={navLinkClass}>Pricing</NavLink>
            <NavLink to="/faq" className={navLinkClass}>FAQ</NavLink>
            <NavLink to="/resources" className={navLinkClass}>Resources</NavLink>
            <NavLink to="/guides" className={navLinkClass}>Guides</NavLink>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/login" className="text-sm font-medium text-gray-700 hover:text-gray-900">
              Sign in
            </Link>
            <Link
              to="/register"
              className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-700"
            >
              Get started
            </Link>
          </div>
        </nav>
      </header>

      {/* flex-col so short pages (e.g. sign-in) can stretch and pin the footer down */}
      <main className="flex flex-1 flex-col">{children}</main>

      {/* Footer */}
      <SiteFooter />
    </div>
  );
}
